import React from 'react';
import { StyleFields, useStyleThemeColors } from '../StyleEditor';
import { RangeInput } from '../map/RasterLayerControls';
import { COLORMAPS, colormapGradient } from '../../lib/raster-api';
import { DEM_PRODUCT_LABELS } from '../../lib/terrain-api';

/**
 * Style tab: vector layers compose StyleEditor's StyleFields over the panel's draft;
 * raster layers drive hooks/useRasterLayers (colormap, range, opacity, DEM product);
 * vector-tile layers pick the colour field the TileJSON advertises (hooks/useVectorTileLayers).
 */

function RasterSection({ layer, raster }) {
  const C = useStyleThemeColors();
  const l = layer.ref;
  if (!raster || !l) return <div style={{ fontSize: 11, color: C.text3 }}>Raster controls are not available.</div>;
  const single = (l.style.bands || []).length === 1 && (!l.style.product || l.style.product === 'elevation');
  const field = { width: '100%', fontSize: 11, padding: '4px 6px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0 };
  const lbl = { fontSize: 10, color: C.text2, display: 'block', marginBottom: 2 };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="raster-style">
      <label style={{ fontSize: 11, color: C.text1, display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="checkbox" checked={!!l.visible} onChange={() => raster.toggleLayer(l.id)} /> Visible
      </label>
      <div>
        <label style={lbl} htmlFor="ugis-raster-opacity">Opacity — {Math.round(l.opacity * 100)}%</label>
        <input id="ugis-raster-opacity" type="range" min={0} max={1} step={0.05} value={l.opacity}
          onChange={(e) => raster.setOpacity(l.id, Number(e.target.value))} style={{ width: '100%', accentColor: C.cyan }} />
      </div>
      {l.dem && (
        <div>
          <label style={lbl} htmlFor="ugis-raster-product">DEM product</label>
          <select id="ugis-raster-product" style={field} value={l.style.product || 'elevation'}
            onChange={(e) => raster.setLayerStyle(l.id, { product: e.target.value })}>
            {['elevation', 'hillshade', 'slope', 'aspect'].map((p) => <option key={p} value={p}>{DEM_PRODUCT_LABELS[p]}</option>)}
          </select>
          {raster.setContours && (
            <label style={{ fontSize: 11, color: C.text1, display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
              <input type="checkbox" checked={!!l.showContours} onChange={(e) => raster.setContours(l.id, e.target.checked)} /> Contours
            </label>
          )}
        </div>
      )}
      {single && (
        <>
          <div>
            <label style={lbl} htmlFor="ugis-raster-colormap">Colormap</label>
            <select id="ugis-raster-colormap" style={field} value={l.style.colormap || 'viridis'}
              onChange={(e) => raster.setLayerStyle(l.id, { colormap: e.target.value })}>
              {COLORMAPS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div style={{ height: 8, borderRadius: 3, marginTop: 6, background: colormapGradient(l.style.colormap) }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div><span style={lbl}>Minimum</span><RangeInput label="Minimum" value={l.style.vmin} onCommit={(v) => raster.setLayerStyle(l.id, { vmin: v })} /></div>
            <div><span style={lbl}>Maximum</span><RangeInput label="Maximum" value={l.style.vmax} onCommit={(v) => raster.setLayerStyle(l.id, { vmax: v })} /></div>
          </div>
        </>
      )}
      {!single && !l.dem && <div style={{ fontSize: 10, color: C.text2 }}>Multi-band rasters render as RGB; only opacity applies.</div>}
    </div>
  );
}

function TilesSection({ layer, vt }) {
  const C = useStyleThemeColors();
  const l = layer.ref;
  if (!vt || !l) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="tiles-style">
      <label style={{ fontSize: 11, color: C.text1, display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="checkbox" checked={!!l.visible} onChange={() => vt.toggleLayer(l.id)} /> Visible
      </label>
      {l.zFields?.length > 0 ? (
        <div>
          <label style={{ fontSize: 10, color: C.text2, display: 'block', marginBottom: 2 }} htmlFor="ugis-tiles-zfield">Colour field (from the TileJSON)</label>
          <select id="ugis-tiles-zfield" value={l.zField || ''} onChange={(e) => vt.setZField(l.id, e.target.value)}
            style={{ width: '100%', fontSize: 11, padding: '4px 6px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0 }}>
            {l.zFields.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
      ) : (
        <div style={{ fontSize: 10, color: C.text2 }}>This tile layer has a fixed colour scheme (apparent resistivity ramp / electrode state).</div>
      )}
    </div>
  );
}

export default function StyleTab({ layer, style, set, info, raster, vt, showHeatmap, setShowHeatmap }) {
  const C = useStyleThemeColors();
  if (layer.kind === 'raster') return <RasterSection layer={layer} raster={raster} />;
  if (layer.kind === 'tiles') return <TilesSection layer={layer} vt={vt} />;
  return (
    <>
      <StyleFields style={style} set={set} info={info} geomType={layer.geomType} />
      {setShowHeatmap && (layer.geomType === 'point' || !layer.geomType) && (
        <label style={{ fontSize: 10, color: C.text2, display: 'flex', alignItems: 'center', gap: 6, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
          <input type="checkbox" checked={!!showHeatmap} onChange={(e) => setShowHeatmap(e.target.checked)} />
          Demo soundings heatmap (sample data)
        </label>
      )}
    </>
  );
}
